/**
 * SecureEmbed - Ingestion Job Detail & Live Progress
 *
 * Shows live progress during RUNNING/STARTING jobs with phase pipeline,
 * event log, and per-source progress. Falls back to config + run history
 * for completed/draft jobs.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft, Edit, Loader2, Clock, Database, RefreshCw,
  ChevronDown, CheckCircle, XCircle, Zap,
  Activity,
} from 'lucide-react';
import { jobsApi } from '../services/api';
import type { RunRecord } from '../services/api';
import { DATASTORE_CARDS, DATASTORE_COLORS } from '../constants/datastores';
import Breadcrumb from '../components/ui/Breadcrumb';

const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-600',
  PENDING: 'bg-yellow-100 text-yellow-700',
  SCHEDULED: 'bg-indigo-100 text-indigo-700',
  STARTING: 'bg-cyan-100 text-cyan-700',
  RUNNING: 'bg-blue-100 text-blue-700',
  COMPLETED: 'bg-green-100 text-green-700',
  FAILED: 'bg-red-100 text-red-700',
  CANCELLED: 'bg-gray-100 text-gray-500',
  PAUSED: 'bg-amber-100 text-amber-700',
};

export default function IngestionJobDetail() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const [expandedRun, setExpandedRun] = useState<string | null>(null);
  const [historyPage, setHistoryPage] = useState(0);
  const eventLogRef = useRef<HTMLDivElement>(null);
  const pageSize = 10;

  const isActive = (status: string) =>
    ['RUNNING', 'STARTING'].includes(status);

  const { data: job } = useQuery({
    queryKey: ['ingestion-job', jobId],
    queryFn: () => jobsApi.getJob(jobId!),
    enabled: !!jobId,
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      return isActive(s || '') ? 3000 : 15000;
    },
  });

  useQuery({
    queryKey: ['job-history', jobId, historyPage],
    queryFn: () => jobsApi.getJobHistory(jobId!, { limit: pageSize, offset: historyPage * pageSize }),
    enabled: !!jobId,
  });

  // Auto-scroll event log
  useEffect(() => {
    if (eventLogRef.current) {
      eventLogRef.current.scrollTop = eventLogRef.current.scrollHeight;
    }
  }, [job]);

  const typedJob = (job || {}) as Record<string, any>;
  const dsColor = DATASTORE_COLORS[job?.target_database || '']?.hex || '#6B7280';
  const dsLabel = DATASTORE_CARDS.find(d => d.key === job?.target_database)?.label || job?.target_database || '';

  const jobIsActive = isActive(job?.status || '');
  const runs = (typedJob.runs || []) as any[];
  const totalRuns = typedJob.total_runs || runs.length;
  const currentPhase = typedJob.current_phase || '';

  // Live progress data
  const PHASES = [
    { key: 'FETCH', label: 'Fetch', icon: Database },
    { key: 'TRANSFORM', label: 'Transform', icon: RefreshCw },
    { key: 'EMBED', label: 'Embed', icon: Loader2 },
    { key: 'INDEX', label: 'Index', icon: Database },
    { key: 'CLEANUP', label: 'Cleanup', icon: CheckCircle },
  ];
  const liveEvents = (typedJob.events || []) as any[];
  const currentSource = typedJob.current_source || '';
  const startedAt = typedJob.started_at ? new Date(typedJob.started_at) : null;
  const elapsed = startedAt ? Math.floor((Date.now() - startedAt.getTime()) / 1000) : 0;
  void PHASES.findIndex(p => p.key === currentPhase.toUpperCase()); // used in JSX
  const phaseDetail = typedJob.phase_detail || '';
  const phasePct = typedJob.phase_progress || 0;

  if (!job) return null;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Breadcrumb */}
      <Breadcrumb items={[
        { label: 'Home', to: '/' },
        { label: 'Ingestion Jobs', to: '/ingestion/jobs' },
        { label: typedJob.job_name || `Job ${job.job_id.slice(0, 8)}` },
      ]} />

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/ingestion/jobs')} className="text-gray-400 hover:text-gray-600">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <Database className="w-6 h-6" style={{ color: dsColor }} />
          <div>
            <h1 className="text-xl font-bold text-gray-900">
              {typedJob.job_name || `Job ${job.job_id.slice(0, 8)}`}
            </h1>
            <span className="text-sm text-gray-500">{dsLabel}</span>
          </div>
          <span className={`ml-2 inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[job.status] || 'bg-gray-100'}`}>
            {jobIsActive && <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
            </span>}
            {job.status}
          </span>
        </div>
        <Link
          to={`/ingestion/wizard/${job.job_id}`}
          className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
        >
          <Edit className="w-3.5 h-3.5" />
          Edit
        </Link>
      </div>

      {/* Error Banner */}
      {job.status === 'FAILED' && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-red-800">Job Failed</h3>
              <p className="text-sm text-red-700 mt-1">{typedJob.error_message || 'Unknown error'}</p>
              {typedJob.ssm_stderr && (
                <details className="mt-2">
                  <summary className="text-xs text-red-600 cursor-pointer hover:text-red-800">Show full error output</summary>
                  <pre className="mt-1 text-xs text-red-600 bg-red-100 p-2 rounded overflow-x-auto max-h-48 whitespace-pre-wrap">{typedJob.ssm_stderr}</pre>
                </details>
              )}
            </div>
          </div>
        </div>
      )}

      {/* SSM Status (when running but no phase progress yet) */}
      {jobIsActive && !currentPhase && typedJob.ssm_status && (
        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-2">
          <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
          <span className="text-sm text-blue-700">
            EC2 execution: <span className="font-medium">{typedJob.ssm_status}</span>
            {typedJob.ssm_last_output && <span className="text-blue-500 ml-2">— {typedJob.ssm_last_output}</span>}
          </span>
        </div>
      )}

      {/* LIVE PROGRESS (when RUNNING/STARTING) */}
      {jobIsActive && (
        <div className="mb-6 space-y-4">
          {/* Phase Pipeline */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-green-400 animate-pulse" />
                <span className="text-sm font-semibold uppercase tracking-wider text-green-400">Live Progress</span>
              </div>
              <div className="flex items-center gap-4 text-xs text-gray-400">
                <span>Elapsed: {formatDuration(elapsed * 1000)}</span>
                <span className="flex items-center gap-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-green-400 animate-pulse"></span>
                  Refreshing every 3s
                </span>
              </div>
            </div>

            {/* Phase Steps */}
            <div className="flex items-center gap-1 mb-5">
              {PHASES.map((phase, i) => {
                const phaseIdx = PHASES.findIndex(p => p.key === currentPhase);
                const isCompleted = i < phaseIdx || currentPhase === 'completed';
                const isCurrent = phase.key === currentPhase;
                const isPending = i > phaseIdx && currentPhase !== 'completed';
                const Icon = phase.icon;

                return (
                  <React.Fragment key={phase.key}>
                    {i > 0 && (
                      <div className={`flex-1 h-0.5 ${isCompleted ? 'bg-green-500' : isCurrent ? 'bg-blue-500' : 'bg-gray-600'}`} />
                    )}
                    <div className={`flex flex-col items-center gap-1 ${isPending ? 'opacity-40' : ''}`}>
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-500 ${
                        isCompleted ? 'bg-green-500/20 border-green-500' :
                        isCurrent ? 'bg-blue-500/20 border-blue-400 ring-2 ring-blue-400/30 animate-pulse' :
                        'bg-gray-700 border-gray-600'
                      }`}>
                        <Icon className={`w-4 h-4 ${
                          isCompleted ? 'text-green-400' :
                          isCurrent ? 'text-blue-300' :
                          'text-gray-500'
                        }`} />
                      </div>
                      <span className={`text-[10px] font-medium uppercase tracking-wider ${
                        isCompleted ? 'text-green-400' :
                        isCurrent ? 'text-blue-300' :
                        'text-gray-500'
                      }`}>{phase.label}</span>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>

            {/* Progress Bar + Stats */}
            <div className="mb-3">
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span className="text-gray-300">{phaseDetail || 'Working...'}</span>
                <span className="text-white font-mono font-bold">{phasePct}%</span>
              </div>
              <div className="h-2.5 bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-blue-500 via-purple-500 to-cyan-400 transition-all duration-1000 ease-out"
                  style={{ width: `${Math.max(phasePct, 1)}%` }}
                />
              </div>
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-4 gap-3 mb-3">
              <div className="bg-gray-800/50 rounded-lg p-2.5 text-center">
                <div className="text-lg font-bold text-white font-mono">
                  {Number(typedJob.progress_pct || phasePct).toFixed(1)}%
                </div>
                <div className="text-[10px] text-gray-400 uppercase">Progress</div>
              </div>
              <div className="bg-gray-800/50 rounded-lg p-2.5 text-center">
                <div className="text-lg font-bold text-cyan-400 font-mono">
                  {Number(typedJob.total_fetched || 0).toLocaleString()}
                </div>
                <div className="text-[10px] text-gray-400 uppercase">Records Fetched</div>
              </div>
              <div className="bg-gray-800/50 rounded-lg p-2.5 text-center">
                <div className="text-lg font-bold text-purple-400 font-mono">
                  {Number(job.total_target_count).toLocaleString()}
                </div>
                <div className="text-[10px] text-gray-400 uppercase">Target</div>
              </div>
              <div className="bg-gray-800/50 rounded-lg p-2.5 text-center">
                <div className="text-lg font-bold text-yellow-400 font-mono">
                  {formatDuration(elapsed * 1000)}
                </div>
                <div className="text-[10px] text-gray-400 uppercase">Elapsed</div>
              </div>
            </div>

            {/* Current Source + SSM Status */}
            <div className="flex items-center justify-between text-xs">
              {currentSource ? (
                <div className="flex items-center gap-2 text-gray-400">
                  <Zap className="w-3 h-3 text-yellow-400" />
                  <span>Active source: <span className="text-yellow-300 font-medium">{currentSource}</span></span>
                </div>
              ) : <span />}
              {typedJob.ssm_status && (
                <span className="text-gray-500">EC2: {typedJob.ssm_status}</span>
              )}
            </div>
          </div>

          {/* Live Event Log — always visible */}
          <div className="bg-gray-900 rounded-xl overflow-hidden shadow-lg border border-gray-700">
            <div className="px-4 py-2.5 bg-gray-800 border-b border-gray-700 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Activity Log</span>
                <span className="text-[10px] bg-gray-700 text-gray-300 px-1.5 py-0.5 rounded-full">{liveEvents.length}</span>
              </div>
              <span className="text-[10px] text-gray-500">Live — refreshing every 3s</span>
            </div>
              <div ref={eventLogRef} className="max-h-64 overflow-y-auto p-3 space-y-1 font-mono text-xs">
                {liveEvents.map((evt, i) => {
                  const time = new Date(evt.ts).toLocaleTimeString('en-AU', { hour12: false });
                  const typeColors: Record<string, string> = {
                    info: 'text-gray-400',
                    success: 'text-green-400',
                    warning: 'text-yellow-400',
                    error: 'text-red-400',
                  };
                  const dotColors: Record<string, string> = {
                    info: 'bg-gray-500',
                    success: 'bg-green-500',
                    warning: 'bg-yellow-500',
                    error: 'bg-red-500',
                  };
                  return (
                    <div key={i} className={`flex items-start gap-2 ${typeColors[evt.type] || 'text-gray-400'}`}>
                      <span className={`mt-1.5 h-1.5 w-1.5 rounded-full flex-shrink-0 ${dotColors[evt.type] || 'bg-gray-500'}`} />
                      <span className="text-gray-500 flex-shrink-0">{time}</span>
                      <span>{evt.msg}</span>
                    </div>
                  );
                })}
              </div>
              {liveEvents.length === 0 && (
                <div className="p-4 text-center text-xs text-gray-500">Waiting for events...</div>
              )}
            </div>

          {/* Parallel Source Activity */}
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Parallel Source Activity</h3>
              <span className="text-[10px] text-gray-400">
                {(() => {
                  const p = typedJob.progress || {};
                  const sources = Object.keys(p).filter(k => k !== '_global');
                  const total = sources.reduce((sum, k) => sum + (Number((p[k] as any)?.fetched) || 0), 0);
                  return `${sources.length} source(s) — ${total.toLocaleString()} total records`;
                })()}
              </span>
            </div>
            {(() => {
              const p = typedJob.progress || {};
              const allSources = (job.sources || []).map((s: any) => s.key || s);
              const activeSources = Object.keys(p).filter(k => k !== '_global');
              // Merge configured sources with active ones
              const sourceKeys = [...new Set([...activeSources, ...allSources])];
              if (sourceKeys.length === 0) {
                return <p className="text-sm text-gray-400 italic">Waiting for sources to start...</p>;
              }
              return (
                <div className="space-y-2">
                  {sourceKeys.map(source => {
                    const stats = (p[source] as any) || {};
                    const fetched = Number(stats?.fetched) || 0;
                    const indexed = Number(stats?.indexed) || 0;
                    const isActive = source === currentSource;
                    const isDone = activeSources.includes(source) && !isActive && fetched > 0;
                    const isPending = !activeSources.includes(source);
                    // Find latest event for this source
                    const sourceEvents = liveEvents.filter(e =>
                      e.msg.toLowerCase().includes(source.toLowerCase()));
                    const lastEvent = sourceEvents[sourceEvents.length - 1]?.msg || '';
                    const targetCount = Number(job.total_target_count) || 1;
                    const sourcePct = Math.min((fetched / targetCount) * 100, 100);

                    return (
                      <div key={source} className={`rounded-lg border p-3 ${
                        isActive ? 'border-blue-300 bg-blue-50' :
                        isDone ? 'border-green-200 bg-green-50' :
                        isPending ? 'border-gray-100 bg-gray-50 opacity-60' :
                        'border-gray-200 bg-white'
                      }`}>
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-2">
                            {isActive && <span className="relative flex h-2 w-2">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
                              <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500" />
                            </span>}
                            {isDone && <CheckCircle className="w-3.5 h-3.5 text-green-500" />}
                            {isPending && <Clock className="w-3.5 h-3.5 text-gray-300" />}
                            <span className="text-sm font-semibold text-gray-800 uppercase">{source}</span>
                            {isActive && <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-medium">FETCHING</span>}
                            {isDone && <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium">DONE</span>}
                            {isPending && <span className="text-[10px] text-gray-400">PENDING</span>}
                          </div>
                          <div className="text-right">
                            <span className="text-sm font-bold text-gray-900 font-mono">{fetched.toLocaleString()}</span>
                            {indexed > 0 && <span className="text-xs text-gray-400 ml-1">/ {indexed.toLocaleString()} indexed</span>}
                          </div>
                        </div>
                        {fetched > 0 && (
                          <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden mb-1">
                            <div className={`h-full rounded-full transition-all duration-1000 ${
                              isActive ? 'bg-blue-500' : 'bg-green-500'
                            }`} style={{ width: `${Math.max(sourcePct, 0.5)}%` }} />
                          </div>
                        )}
                        {lastEvent && (
                          <div className="text-[10px] text-gray-500 truncate">{lastEvent}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Job Configuration */}
      <div className="bg-white rounded-lg border border-gray-200 p-5 mb-6">
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Job Configuration</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <ConfigRow label="Status" value={job.status} />
          <ConfigRow label="Schedule" value={typedJob.schedule_expression || 'None'} />
          <ConfigRow
            label="Sources"
            value={
              typedJob.source_proportions
                ? Object.entries(typedJob.source_proportions).map(([k, v]) => `${k} (${v}%)`).join(', ')
                : job.sources?.map((s: any) => s.label || s.key).join(', ') || 'None'
            }
          />
          <ConfigRow
            label="Topics"
            value={
              typedJob.topic_proportions
                ? Object.entries(typedJob.topic_proportions).map(([k, v]) => `${k} (${v}%)`).join(', ')
                : 'All content'
            }
          />
          <ConfigRow label="Embedding Model" value={typedJob.embedding_config?.model_id || '--'} />
          <ConfigRow label="Cleanup" value={typedJob.cleanup_raw_data ? 'Yes' : 'No'} />
          <ConfigRow label="Created" value={job.created_at ? new Date(job.created_at).toLocaleString() : '--'} />
          <ConfigRow label="Updated" value={job.updated_at ? new Date(job.updated_at).toLocaleString() : '--'} />
        </div>
      </div>

      {/* Run History */}
      <div className="bg-white rounded-lg border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
            <Clock className="w-4 h-4 inline mr-1" />
            Run History ({totalRuns} runs)
          </h2>
        </div>

        {runs.length === 0 ? (
          <p className="text-sm text-gray-400 italic py-4">No runs recorded yet.</p>
        ) : (
          <>
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 uppercase w-8"></th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 uppercase">Run ID</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 uppercase">Start</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 uppercase">End</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 uppercase">Duration</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="text-right px-3 py-2 text-xs font-medium text-gray-500 uppercase">Records</th>
                  <th className="text-right px-3 py-2 text-xs font-medium text-gray-500 uppercase">Errors</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((run: RunRecord) => {
                  const isExpanded = expandedRun === run.run_id;
                  const duration = run.start_time && run.end_time
                    ? formatDuration(new Date(run.end_time).getTime() - new Date(run.start_time).getTime())
                    : '--';
                  return (
                    <React.Fragment key={run.run_id}>
                      <tr
                        className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                        onClick={() => setExpandedRun(isExpanded ? null : run.run_id)}
                      >
                        <td className="px-3 py-2">
                          <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExpanded ? '' : '-rotate-90'}`} />
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-600 font-mono">{run.run_id.slice(0, 8)}</td>
                        <td className="px-3 py-2 text-sm text-gray-500">{run.start_time ? new Date(run.start_time).toLocaleString() : '--'}</td>
                        <td className="px-3 py-2 text-sm text-gray-500">{run.end_time ? new Date(run.end_time).toLocaleString() : '--'}</td>
                        <td className="px-3 py-2 text-sm text-gray-500">{duration}</td>
                        <td className="px-3 py-2">
                          <span className="inline-flex items-center gap-1 text-xs">
                            {run.status === 'COMPLETED' ? (
                              <CheckCircle className="w-3 h-3 text-green-500" />
                            ) : (
                              <XCircle className="w-3 h-3 text-red-500" />
                            )}
                            {run.status}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-600 text-right">{run.records_processed?.toLocaleString() || 0}</td>
                        <td className="px-3 py-2 text-sm text-right">
                          <span className={run.records_failed > 0 ? 'text-red-600 font-medium' : 'text-gray-400'}>
                            {run.records_failed || 0}
                          </span>
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr>
                          <td colSpan={8} className="bg-gray-50 px-6 py-4">
                            {run.per_source_stats && Object.keys(run.per_source_stats).length > 0 && (
                              <div className="mb-3">
                                <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Per-Source Breakdown</h4>
                                <div className="grid grid-cols-3 gap-2">
                                  {Object.entries(run.per_source_stats).map(([source, stats]: [string, any]) => (
                                    <div key={source} className="bg-white rounded border border-gray-200 p-2">
                                      <div className="text-xs font-medium text-gray-700">{source}</div>
                                      <div className="text-xs text-gray-500">
                                        {typeof stats === 'object'
                                          ? `${stats.processed || 0} processed, ${stats.errors || 0} errors`
                                          : `${stats} records`}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Error messages */}
                            {run.errors && run.errors.length > 0 && (
                              <div>
                                <h4 className="text-xs font-medium text-red-500 uppercase mb-2">Errors ({run.errors.length})</h4>
                                <div className="space-y-1 max-h-40 overflow-y-auto">
                                  {run.errors.map((err: string, i: number) => (
                                    <div key={i} className="text-xs text-red-600 bg-red-50 px-2 py-1 rounded font-mono">{err}</div>
                                  ))}
                                </div>
                              </div>
                            )}
                            {(!run.per_source_stats || Object.keys(run.per_source_stats).length === 0) &&
                             (!run.errors || run.errors.length === 0) && (
                              <p className="text-xs text-gray-400 italic">No additional details available.</p>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>

            {totalRuns > pageSize && (
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                <button
                  onClick={() => setHistoryPage(p => Math.max(0, p - 1))}
                  disabled={historyPage === 0}
                  className="text-sm text-blue-600 hover:text-blue-800 disabled:text-gray-300"
                >
                  Previous
                </button>
                <span className="text-xs text-gray-500">
                  Page {historyPage + 1} of {Math.ceil(totalRuns / pageSize)}
                </span>
                <button
                  onClick={() => setHistoryPage(p => p + 1)}
                  disabled={(historyPage + 1) * pageSize >= totalRuns}
                  className="text-sm text-blue-600 hover:text-blue-800 disabled:text-gray-300"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ConfigRow({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div className="flex flex-col">
      <span className="text-xs text-gray-400">{label}</span>
      <span className="text-sm font-medium truncate" style={color ? { color } : undefined}>{value}</span>
    </div>
  );
}

function formatDuration(ms: number): string {
  if (ms < 0 || isNaN(ms)) return '--';
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remSec = seconds % 60;
  if (minutes < 60) return `${minutes}m ${remSec}s`;
  const hours = Math.floor(minutes / 60);
  const remMin = minutes % 60;
  return `${hours}h ${remMin}m`;
}
